import React from 'react';
import PublicNavbar from '../../components/Navigation/PublicNavbar';
import { 
  Target, 
  Eye, 
  Heart, 
  Users, 
  Trophy, 
  Zap, 
  Brain, 
  Globe,
  Award,
  TrendingUp,
  Shield,
  Rocket
} from 'lucide-react';

interface TeamMember {
  name: string;
  role: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface Stat {
  value: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface Value {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

const NosotrosPage: React.FC = () => {
  const stats: Stat[] = [
    {
      value: "10,000+",
      label: "Jugadores Analizados",
      icon: Users
    },
    {
      value: "500+",
      label: "Clubes Activos",
      icon: Trophy
    },
    {
      value: "1M+",
      label: "Horas de Video Procesadas",
      icon: Zap
    },
    {
      value: "99.9%",
      label: "Precisión en Análisis IA",
      icon: Brain
    }
  ];

  const values: Value[] = [
    {
      title: "Innovación Constante",
      description: "Desarrollamos tecnología de vanguardia para revolucionar el scouting deportivo con IA y machine learning.",
      icon: Rocket
    },
    {
      title: "Precisión y Confiabilidad",
      description: "Nuestros algoritmos garantizan análisis precisos y datos confiables para la toma de decisiones estratégicas.",
      icon: Target
    },
    {
      title: "Accesibilidad Global",
      description: "Democratizamos el acceso a tecnología avanzada de scouting para clubes de todos los tamaños y niveles.",
      icon: Globe
    },
    {
      title: "Excelencia en Servicio",
      description: "Brindamos soporte excepcional y acompañamiento personalizado a cada uno de nuestros clientes.",
      icon: Award
    }
  ];

  const team: TeamMember[] = [
    {
      name: "Equipo de Ingeniería",
      role: "Desarrollo de IA y Machine Learning",
      description: "Expertos en visión por computadora, procesamiento de video y algoritmos de análisis deportivo.",
      icon: Brain
    },
    {
      name: "Especialistas Deportivos",
      role: "Análisis y Metodología",
      description: "Ex-jugadores profesionales y entrenadores que aportan conocimiento técnico del fútbol.",
      icon: Trophy
    },
    {
      name: "Equipo de Producto",
      role: "UX/UI y Experiencia de Usuario",
      description: "Diseñadores y desarrolladores enfocados en crear interfaces intuitivas y funcionales.",
      icon: Users
    },
    {
      name: "Soporte y Éxito del Cliente",
      role: "Atención y Acompañamiento",
      description: "Especialistas dedicados a garantizar el éxito y satisfacción de nuestros usuarios.",
      icon: Heart
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700">
      <PublicNavbar />
      
      <div className="pt-20 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-7xl mx-auto text-center py-20">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-8">
            Sobre <span className="text-blue-300">ScoutMind AI</span>
          </h1>
          <p className="text-xl text-blue-100 max-w-4xl mx-auto leading-relaxed">
            Somos pioneros en la aplicación de inteligencia artificial al scouting deportivo, 
            transformando la manera en que los clubes descubren, analizan y desarrollan talento futbolístico.
          </p>
        </div>

        {/* Mission & Vision */}
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
            <div className="flex items-center mb-6">
              <div className="bg-blue-600 rounded-lg p-3 mr-4">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white">Nuestra Misión</h2>
            </div>
            <p className="text-blue-100 text-lg leading-relaxed">
              Democratizar el acceso a tecnología avanzada de scouting deportivo, proporcionando 
              herramientas de inteligencia artificial que permitan a clubes de todos los niveles 
              identificar, analizar y desarrollar talento de manera más eficiente y precisa.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
            <div className="flex items-center mb-6">
              <div className="bg-indigo-600 rounded-lg p-3 mr-4">
                <Eye className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-white">Nuestra Visión</h2>
            </div>
            <p className="text-blue-100 text-lg leading-relaxed">
              Ser la plataforma líder mundial en análisis deportivo con IA, revolucionando 
              la industria del fútbol mediante tecnología innovadora que transforme la manera 
              en que se descubre, evalúa y desarrolla el talento futbolístico.
            </p>
          </div>
        </div>

        {/* Statistics */}
        <div className="max-w-7xl mx-auto mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Impacto en <span className="text-blue-300">Números</span>
            </h2>
            <p className="text-xl text-blue-100">
              Resultados que demuestran nuestro compromiso con la excelencia
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-8 text-center border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105"
                >
                  <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                  <div className="text-blue-100 font-medium">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Values */}
        <div className="max-w-7xl mx-auto mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Nuestros <span className="text-blue-300">Valores</span>
            </h2>
            <p className="text-xl text-blue-100">
              Los principios que guían cada decisión y desarrollo en ScoutMind AI
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-lg p-3 flex-shrink-0">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                      <p className="text-blue-100 leading-relaxed">{value.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Technology Stack */}
        <div className="max-w-7xl mx-auto mb-20">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                Tecnología de <span className="text-blue-300">Vanguardia</span>
              </h2>
              <p className="text-xl text-blue-100">
                Utilizamos las tecnologías más avanzadas para ofrecer análisis precisos y confiables
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <Brain className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Inteligencia Artificial</h3>
                <p className="text-blue-100">
                  Algoritmos de machine learning y deep learning para análisis predictivo y 
                  reconocimiento de patrones en el rendimiento deportivo.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <Eye className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Visión por Computadora</h3>
                <p className="text-blue-100">
                  YOLO v8 y OpenCV para detección automática de jugadores, seguimiento 
                  de movimientos y extracción de métricas físicas en tiempo real.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-gradient-to-br from-purple-600 to-pink-700 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Análisis Predictivo</h3>
                <p className="text-blue-100">
                  Modelos estadísticos avanzados para predicción de rendimiento, 
                  identificación de talento y análisis de potencial de desarrollo.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Team */}
        <div className="max-w-7xl mx-auto mb-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Nuestro <span className="text-blue-300">Equipo</span>
            </h2>
            <p className="text-xl text-blue-100">
              Profesionales apasionados por la tecnología y el deporte
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {team.map((member, index) => {
              const Icon = member.icon;
              return (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-lg p-3 flex-shrink-0">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">{member.name}</h3>
                      <p className="text-blue-300 font-medium mb-3">{member.role}</p>
                      <p className="text-blue-100 leading-relaxed">{member.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Call to Action */}
        <div className="max-w-4xl mx-auto text-center pb-20">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-12 border border-white/20">
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-6">
              ¿Listo para Revolucionar tu Scouting?
            </h2>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Únete a los cientos de clubes que ya confían en ScoutMind AI para 
              descubrir y desarrollar el talento del futuro.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white font-semibold rounded-xl transition-all duration-200 transform hover:scale-105 focus:ring-4 focus:ring-blue-500/50 focus:outline-none">
                Solicitar Demo
              </button>
              <button className="px-8 py-4 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-xl border border-white/30 transition-all duration-200 transform hover:scale-105 focus:ring-4 focus:ring-white/50 focus:outline-none">
                Ver Precios
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NosotrosPage;
